import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoLeo = () => {
  const articleSchema = createArticleSchema(
    "Leo: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Leo. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de julio y el 22 de agosto.",
    `${window.location.origin}/horoscopo-leo`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Leo",
        "item": `${window.location.origin}/horoscopo-leo`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Leo" }
  ];

  return (
    <>
      <Helmet>
        <title>Leo: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Leo. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de julio y el 22 de agosto." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-leo`} />
        <meta property="og:title" content="Leo: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Leo. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-leo`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♌</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Leo
              </h1>
              <p className="text-xl text-muted-foreground">23 de Julio - 22 de Agosto</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Leo: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="leo" signName="Leo" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Leo: El Rey Creativo</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Leo</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">🔥 Elemento: Fuego</h4>
                    <p className="text-muted-foreground">Enfoque en la pasión, el ego y la expresión. Leo irradia calor y energía vital.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">👑 Modalidad: Fijo</h4>
                    <p className="text-muted-foreground">Define la constancia, la nobleza y la necesidad de reconocimiento. Firme en sus convicciones.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">☀️ Astro Regente: El Sol</h4>
                    <p className="text-muted-foreground">Símbolo de la Vitalidad, el Centro y la Consciencia. Fuente de luz y vida.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Leo: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Carisma:</strong> Magnetismo natural irresistible</li>
                      <li>• <strong>Liderazgo:</strong> Capacidad innata para dirigir</li>
                      <li>• <strong>Generosidad:</strong> Corazón noble y dadivoso</li>
                      <li>• <strong>Confianza:</strong> Fe inquebrantable en sí mismo</li>
                      <li>• <strong>Creatividad:</strong> Expresión artística poderosa</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Arrogancia:</strong> Exceso de orgullo</li>
                      <li>• <strong>Egocentrismo:</strong> Necesidad constante de atención</li>
                      <li>• <strong>Vanidad:</strong> Preocupación por la imagen</li>
                      <li>• <strong>Drama:</strong> Tendencia a exagerar situaciones</li>
                      <li>• <strong>Terquedad:</strong> Dificultad para admitir errores</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Quiero"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Leo en el Amor: Romance, Lealtad y Reconocimiento</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Leo ama con la intensidad del sol del mediodía. Busca un romance digno de un cuento de hadas, lleno de pasión, drama y gestos grandiosos. Valora la lealtad por encima de todo y espera devoción absoluta de su pareja. El romance para Leo debe ser una celebración constante del amor.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una pareja que lo admire y reconozca su grandeza, pero que también sea lo suficientemente fuerte para mantener su propia identidad. Leo es increíblemente generoso con sus seres queridos, dispuesto a dar el mundo a quien capture su corazón. Su amor es protector, apasionado y leal hasta el final.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Leo en el Trabajo y Finanzas: Foco en la Dirección y las Artes</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Leo destaca en posiciones de liderazgo, entretenimiento, artes escénicas, diseño, joyería de lujo y cualquier carrera que le permita brillar y ser reconocido. Su creatividad y carisma lo hacen natural en campos como la actuación, la dirección y la organización de eventos de alto perfil.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Leo es generoso pero también le gusta el lujo y la calidad. Invierte en cosas que reflejen su estatus y creatividad. Puede ser impulsivo con el dinero cuando se trata de impresionar o disfrutar la vida. Excelente para negocios relacionados con entretenimiento, lujo y creatividad.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Leo con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♈ Aries y ♐ Sagitario</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego comparten pasión, entusiasmo y amor por la aventura. Con Aries hay química explosiva y admiración mutua. Con Sagitario, una conexión de aventura compartida y optimismo contagioso.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♊ Géminis y ♎ Libra</h4>
                    <p className="text-muted-foreground">
                      Los signos de aire alimentan el fuego de Leo. Géminis aporta diversión y estimulación mental, mientras que Libra ofrece equilibrio, belleza y apreciación del romance que Leo anhela.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Leo</h3>
                <p className="text-muted-foreground mb-4">
                  El servicio humilde es el gran desafío de Leo. Los signos de tierra (Tauro, Virgo, Capricornio) le enseñan sobre trabajo duro sin necesidad de reconocimiento constante, y el valor de la humildad y la practicidad.
                </p>
                <p className="text-muted-foreground">
                  Acuario, su signo opuesto, lo desafía a considerar el bien colectivo sobre el ego personal. Los signos de agua (Cáncer, Escorpio, Piscis) le enseñan sobre vulnerabilidad emocional y la profundidad más allá del drama superficial.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoLeo;
